import React, { useState, useEffect, useCallback } from 'react'
import {
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  InputLabel,
} from '@material-ui/core'
import { MultiSelect } from 'react-multi-select-component'

import { Dialog, ImageUploadField, snackbar } from 'components'
import { useCheckFormErrors, useApiCall } from 'hooks'
import { ERRORS } from 'consts'
import { ApiError } from 'errors'
import { resizeFile } from 'utils'

import { createStore, getActiveTags } from '../api'
import { Store, CreateStorePayload } from '../types'

interface CreateStoresDialogProps {
  handleClose: () => void
  onDone: (newStore: Store) => void
}

const CreateStoresDialog: React.FC<CreateStoresDialogProps> = ({ handleClose, onDone }) => {
  const [name, setName] = useState('')
  const [secondaryName, setSecondaryName] = useState('')
  const [description, setDescription] = useState('')
  const [usdEnabled, setUsdEnabled] = useState(false)
  const [pointsEnabled, setPointsEnabled] = useState(true)
  const [active, setActive] = useState(true)
  const [rut, setRut] = useState('')
  const [serie, setSerie] = useState('')

  const [tags, setTags] = useState<{ label: string; value: number }[]>([])
  const [selectedTags, setSelectedTags] = useState<{ label: string; value: number }[]>([])

  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState('')
  const [storeImageFile, setStoreImageFile] = useState<File | null>(null)
  const [storeImagePreview, setStoreImagePreview] = useState('')

  const getTagsRequest = useCallback(() => getActiveTags(), [])
  const [fetchTags] = useApiCall<void, { tags: { id: number; title: string }[] }>(
    getTagsRequest,
  )

  useEffect(() => {
    const loadTags = async () => {
      try {
        const response = await fetchTags()
        const mappedTags = response.tags.map(tag => ({
          label: tag.title,
          value: tag.id,
        }))
        setTags(mappedTags)
      } catch (err) {
        console.error('Error al obtener tags', err)
      }
    }
    loadTags()
  }, [fetchTags])

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)
  const handleSecondaryNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSecondaryName(e.target.value)
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setDescription(e.target.value)
  const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '')
    setRut(value)
  }
  const handleUsdEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setUsdEnabled(e.target.checked)
  const handlePointsEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setPointsEnabled(e.target.checked)
  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => setActive(e.target.checked)

  const handleFileChange = async (file: File, type: 'logo' | 'storeImage') => {
    const previewUrl = URL.createObjectURL(file)
    if (type === 'logo') {
      setLogoPreview(previewUrl)
      const image = await resizeFile(file, 192, 192)
      setLogoFile(image)
    } else {
      setStoreImagePreview(previewUrl)
      const image = await resizeFile(file, 600, 600)
      setStoreImageFile(image)
    }
  }

  const handleRemoveImage = (type: 'logo' | 'storeImage') => {
    if (type === 'logo') {
      setLogoFile(null)
      setLogoPreview('')
    } else {
      setStoreImageFile(null)
      setStoreImagePreview('')
    }
  }

  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(
    { name, secondaryName, rut, selectedTags, logoFile, storeImageFile, description },
    {
      name: [
        { validate: (val: string) => val.trim().length > 0, message: 'El nombre es requerido' },
        {
          validate: (val: string) => val.length >= 5,
          message: 'El nombre debe tener al menos 5 caracteres',
        },
      ],
      secondaryName: [
        {
          validate: (val: string) => val.trim().length > 0,
          message: 'El nombre secundario es requerido',
        },
        {
          validate: (val: string) => val.length >= 5,
          message: 'El nombre secundario debe tener al menos 5 caracteres',
        },
      ],
      rut: [
        { validate: (val: string) => val.trim().length > 0, message: 'El RUT es requerido' },
        { validate: (val: string) => /^\d+$/.test(val), message: 'El RUT debe ser numérico' },
        {
          validate: (val: string) => val.length >= 5,
          message: 'El RUT debe tener al menos 5 caracteres',
        },
        {
          validate: (val: string) => val.length <= 40,
          message: 'El RUT no debe superar los 40 caracteres',
        },
      ],
      selectedTags: [
        {
          validate: (val: { label: string; value: number }[]) => val.length > 0,
          message: 'Debe seleccionar al menos una etiqueta',
        },
      ],
      logoFile: [
        {
          validate: (val: File | null) => val !== null,
          message: 'Debe subir un logo',
        },
      ],
      storeImageFile: [
        {
          validate: (val: File | null) => val !== null,
          message: 'Debe subir una imagen de la tienda',
        },
      ],
      description: [
        {
          validate: (val: string) => val.trim().length > 0,
          message: 'La descripción es requerida',
        },
        {
          validate: (val: string) => val.length <= 20,
          message: 'La descripción no debe superar los 20 caracteres',
        },
      ],
    },
  )

  const isSubmitDisabled = hasErrors || isAnyFieldEmpty

  const [createStoreApi, isLoading] = useApiCall<CreateStorePayload, Store>(createStore)

  const handleSubmit = async () => {
    try {
      const tagIds = selectedTags.map(tag => tag.value)

      const storeData: CreateStorePayload = {
        name,
        secondaryName,
        description,
        usdEnabled,
        pointsEnabled,
        active,
        rut,
        serie: serie.trim() || undefined,
        tagIds,
        logoUrl: logoFile ?? undefined,
        storeImageUrl: storeImageFile ?? undefined,
      }

      const newStore = await createStoreApi(storeData)
      onDone(newStore)
    } catch (err) {
      console.error(err)
      const message = err instanceof ApiError ? err.message : ERRORS.GENERIC_ERROR_MESSAGE
      const variant = err instanceof ApiError && err.status < 400 ? 'success' : 'error'
      snackbar.show(message, variant)
    }
  }

  return (
    <Dialog
      title="Nueva Marca"
      isOpen
      onCancel={handleClose}
      showActions
      okButtonText="Crear Marca"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={handleSubmit}
      isLoading={isLoading}
      contentStyle={{ minWidth: 500, overflowY: 'scroll' }}
      overflow
      style={{ minHeight: '50%' }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Razón social"
            required
            value={name}
            onChange={handleNameChange}
            error={errors.name?.hasError}
            helperText={errors.name?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Nombre secundario"
            required
            value={secondaryName}
            onChange={handleSecondaryNameChange}
            error={errors.secondaryName?.hasError}
            helperText={errors.secondaryName?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Descripción"
            required
            value={description}
            onChange={handleDescriptionChange}
            error={errors.description?.hasError}
            helperText={errors.description?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <FormControlLabel
            control={<Checkbox checked={usdEnabled} onChange={handleUsdEnabledChange} />}
            label="Habilitar USD"
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            control={<Checkbox checked={active} onChange={handleActiveChange} />}
            label="Activo"
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            control={<Checkbox checked={pointsEnabled} onChange={handlePointsEnabledChange} />}
            label="Suma puntos"
          />
        </Grid>

        <Grid item xs={8}>
          <TextField
            fullWidth
            label="RUT"
            required
            value={rut}
            onChange={handleRutChange}
            error={errors.rut?.hasError}
            helperText={errors.rut?.message}
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Serie"
            value={serie}
            onChange={e => setSerie(e.target.value.toUpperCase())}
            helperText="Solo si esta marca comparte RUT con otra"
          />
        </Grid>

        <Grid item xs={12}>
          <InputLabel>Etiquetas</InputLabel>
          <MultiSelect
            options={tags}
            value={selectedTags}
            onChange={setSelectedTags}
            labelledBy="Seleccionar"
            hasSelectAll={false}
          />
          {errors.selectedTags?.hasError && (
            <Typography color="error" variant="caption">
              {errors.selectedTags.message}
            </Typography>
          )}
        </Grid>

        <Grid item xs={6}>
          <ImageUploadField
            id="logo-upload0"
            label="Logo"
            preview={logoPreview}
            onChange={file => handleFileChange(file, 'logo')}
            onDelete={() => handleRemoveImage('logo')}
          />
        </Grid>

        <Grid item xs={6}>
          <ImageUploadField
            id="store-image-upload"
            label="Imagen de la tienda"
            variant="wide"
            preview={storeImagePreview}
            onChange={file => handleFileChange(file, 'storeImage')}
            onDelete={() => handleRemoveImage('storeImage')}
          />
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { CreateStoresDialog }
